import React, { useState } from 'react';
import classNames from 'classnames';

import { FormattedMessage } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { isTermFormat } from '../../../util/createClass';
import { showsAsTerm } from '../../../util/termSchedule';
import { zoneLabelMaybe } from '../../../util/dates';
import { computeChildAge, getChildren } from '../../../util/childData';
import { isAgeWithinRange } from '../../../util/listingData';
import { Modal } from '../../../components';

import { dayCountAvailableForBooking } from '../../../config/configStripe';
import { daysOfferedLabel, cardTimeInfo } from '../../../components/ListingCard/ListingCard';
import ClassSessionPicker from './ClassSessionPicker';
import {
  flattenMonthlyTimeSlots,
  slotsWithinDays,
  upcomingSlotDays,
  slotSeats,
  listingTimeZone,
  classBookableFromMs,
  termScheduleSummary,
} from './classDetailData';

import css from './ClassDetail.module.css';
import { termWeeks } from '../../../util/termSchedule';

const timeZoneMaybe = timeZone => (timeZone ? { timeZone } : {});

const CheckIcon = () => (
  <svg className={css.childPillCheck} viewBox="0 0 24 24" role="none">
    <path d="M20 6L9 17l-5-5" fill="none" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * The "Activity Booking" modal (Figma) -- the KidHive presentation over the UNTOUCHED booking
 * engine. Single-session: pick a day on a month calendar (any future date the provider has
 * scheduled, up to the time-slots query horizon), then a real time slot (true seats remaining);
 * stepping to an unfetched month lazily fetches its availability. Term: the enrollment window is
 * the one Fixed booking, nothing to pick. "Who is attending" selects one of the parent's children;
 * the id rides through orderData into the checkout, where the Phase 2 bookedChild snapshot is
 * attached at initiate time. Book now submits the same value names the stock
 * BookingFixedDurationForm produces (bookingStartTime/bookingEndTime timestamps), so the
 * handleSubmit -> CheckoutPage seam stays byte-identical; payment itself is the checkout task.
 */
const ClassBookingModal = props => {
  const {
    isOpen,
    onClose,
    listing,
    currentUser,
    monthlyTimeSlots,
    intl,
    onSubmit,
    onFetchTimeSlots,
    onManageDisableScrolling,
    // Preselection from the on-page schedule card. The caller keys this component by these values,
    // so a new preselection remounts with fresh state -- no state-syncing effect needed.
    initialDayKey = null,
    initialSlotKey = null,
  } = props;

  const children = getChildren(currentUser).filter(child => child.id);

  // AGE GATE (client ask): a child outside the class's age band can't be selected or booked.
  // Checked only when the listing carries a band AND the child has a birth year -- when either
  // side is unknown we allow rather than guess (the provider screens at the door). Server-side
  // enforcement at initiate is Aleksa's mirror of this, like the advance-booking cap.
  const bandSource = listing?.attributes?.publicData || {};
  const toAgeBand = v => {
    const n = typeof v === 'string' && v.trim() !== '' ? Number(v) : v;
    return Number.isInteger(n) ? n : null;
  };
  const ageBandMin = toAgeBand(bandSource.ageBandMin);
  const ageBandMax = toAgeBand(bandSource.ageBandMax);
  const hasAgeBand = ageBandMin !== null && ageBandMax !== null;
  const isChildEligible = child => {
    if (!hasAgeBand) {
      return true;
    }
    const age = computeChildAge(child);
    return age === null ? true : isAgeWithinRange(age, ageBandMin, ageBandMax);
  };
  const eligibleChildren = children.filter(isChildEligible);
  const someIneligible = children.length > eligibleChildren.length;
  const noneEligible = children.length > 0 && eligibleChildren.length === 0;
  const [selectedDayKey, setSelectedDayKey] = useState(initialDayKey);
  const [selectedSlotKey, setSelectedSlotKey] = useState(initialSlotKey);
  const [attendeeChildId, setAttendeeChildId] = useState(eligibleChildren[0]?.id || null);

  const { price, publicData = {} } = listing.attributes || {};
  const isTerm = isTermFormat(publicData.classFormat);
  const timeZone = listingTimeZone(listing);

  const slots = isTerm
    ? []
    : slotsWithinDays(
        flattenMonthlyTimeSlots(monthlyTimeSlots, new Date(), classBookableFromMs(listing)),
        dayCountAvailableForBooking
      );
  const days = upcomingSlotDays(slots, { maxDays: Infinity, timeZone });
  const selectedDay = days.find(d => d.dayKey === selectedDayKey) || days[0] || null;
  const slotKeyOf = slot => slot.attributes.start.toISOString();
  const selectedSlot =
    selectedDay?.slots.find(slot => slotKeyOf(slot) === selectedSlotKey) || null;

  const term = isTerm ? termScheduleSummary(listing) : null;
  // A one-session term (the old one-off-class workaround) books as a term but reads as a drop-in:
  // its one date + time, priced per session, no "every session in the term" copy.
  const isOneSession = isTerm && !showsAsTerm(publicData);
  // Days + shared-time labels for the term summary (same helpers as the class cards).
  const termDaysLabel = isTerm ? daysOfferedLabel(publicData.scheduleInput) : null;
  const termTimeInfo = isTerm ? cardTimeInfo(publicData.scheduleInput) : null;
  const termTimeLabel = !termTimeInfo
    ? null
    : termTimeInfo.various
    ? intl.formatMessage({ id: 'ListingCard.variousTimes' })
    : intl.formatTime(
        new Date(
          2000,
          0,
          1,
          Number(termTimeInfo.time.slice(0, 2)),
          Number(termTimeInfo.time.slice(3, 5))
        ),
        { hour: 'numeric', minute: '2-digit' }
      );

  const priceFormatted = price && price.amount != null ? formatMoney(intl, price) : null;
  const seatsLine = isTerm ? null : slotSeats(selectedSlot) ?? slotSeats(selectedDay?.slots[0]);

  // Advance-booking cap: a term whose FIRST session is beyond the horizon isn't open for booking yet
  // (the charge would age past the refund-safe window). Drop-ins can't select a beyond-cap slot at all
  // -- the picker doesn't show them -- so only terms need this explicit gate.
  const MS_PER_DAY = 24 * 60 * 60 * 1000;
  const termWithinCap =
    !isTerm ||
    (term?.start instanceof Date &&
      term.start.getTime() <= Date.now() + dayCountAvailableForBooking * MS_PER_DAY);
  const canSubmit =
    (isTerm ? !!term && term.sessions.length > 0 && termWithinCap : !!selectedSlot) &&
    !noneEligible;

  const handleBookNow = () => {
    if (!canSubmit) {
      return;
    }
    // TERM: book the FIRST session's slot. The availability plan only contains the weekly
    // session-length entries (never a continuous multi-week window), so booking term.start ->
    // term.end always failed speculation with "time not available". The `fixed` unit charges the
    // term price per unit regardless of duration; the seat cap and the cancellation pivot are both
    // keyed to the first session already, so nothing else changes.
    const window = isTerm
      ? { start: term.sessions[0].start, end: term.sessions[0].end }
      : { start: selectedSlot.attributes.start, end: selectedSlot.attributes.end };
    const attendeeMaybe = attendeeChildId ? { attendeeChildId } : {};
    // The exact value names the stock fixed-duration booking form submits -- handleSubmit turns the
    // timestamps into orderData.bookingDates and spreads the rest (attendeeChildId) into orderData.
    onSubmit({
      bookingStartTime: window.start.getTime(),
      bookingEndTime: window.end.getTime(),
      ...attendeeMaybe,
    });
  };

  return (
    <Modal
      id="ClassDetail.booking"
      containerClassName={css.bookingModalContainer}
      isOpen={isOpen}
      onClose={onClose}
      usePortal
      onManageDisableScrolling={onManageDisableScrolling}
    >
      <h2 className={css.bookingModalTitle}>
        <FormattedMessage id="ClassDetailPage.bookingModalTitle" />
      </h2>
      <p className={css.sectionSubtitle}>
        <FormattedMessage
          id={
            isOneSession
              ? 'ClassDetailPage.scheduleSubtitleOneSession'
              : isTerm
              ? 'ClassDetailPage.scheduleSubtitleTerm'
              : 'ClassDetailPage.scheduleSubtitle'
          }
        />
      </p>

      <div className={css.bookingModalColumns}>
        <div className={css.bookingModalMain}>
          {isTerm ? (
            term && isOneSession ? (
              <div className={css.termSummaryRow}>
                <span className={css.termWindowChip}>
                  {intl.formatDate(term.start, {
                    weekday: 'short',
                    month: 'short',
                    day: 'numeric',
                    ...timeZoneMaybe(timeZone),
                  })}
                  {' · '}
                  {intl.formatTime(term.start, zoneLabelMaybe(timeZone))}
                </span>
              </div>
            ) : term ? (
              <div className={css.termSummaryRow}>
                <span className={css.termWindowChip}>
                  <FormattedMessage
                    id="ClassDetailPage.termWindow"
                    values={{
                      start: intl.formatDate(term.start, {
                        month: 'short',
                        day: 'numeric',
                        ...timeZoneMaybe(timeZone),
                      }),
                      end: intl.formatDate(term.end, {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric',
                        ...timeZoneMaybe(timeZone),
                      }),
                    }}
                  />
                </span>
                <span className={css.termMeta}>
                  <FormattedMessage
                    id="ClassDetailPage.termMeta"
                    values={{ count: term.sessionCount }}
                  />
                </span>
                {/* The weekly rhythm (client report: the popup showed only the date window, not
                    which days/hours the multi-week class actually runs). */}
                {termDaysLabel ? (
                  <span className={css.termDaysLine}>
                    {termDaysLabel}
                    {termTimeLabel ? ` · ${termTimeLabel}` : ''}
                  </span>
                ) : null}
              </div>
            ) : (
              <p className={css.scheduleEmpty}>
                <FormattedMessage id="ClassDetailPage.noUpcomingSessions" />
              </p>
            )
          ) : (
            <ClassSessionPicker
              listing={listing}
              monthlyTimeSlots={monthlyTimeSlots}
              intl={intl}
              selectedDayKey={selectedDayKey}
              selectedSlotKey={selectedSlotKey}
              onSelectDay={key => {
                setSelectedDayKey(key);
                setSelectedSlotKey(null);
              }}
              onSelectSlot={key => setSelectedSlotKey(key)}
              onFetchTimeSlots={onFetchTimeSlots}
            />
          )}

          {children.length > 0 ? (
            <>
              <p className={css.whoAttendsLabel}>
                <FormattedMessage id="ClassDetailPage.whoAttends" />
              </p>
              <div className={css.childPillRow}>
                {children.map(child => {
                  const isSelected = child.id === attendeeChildId;
                  const eligible = isChildEligible(child);
                  return (
                    <button
                      key={child.id}
                      type="button"
                      className={classNames(css.childPill, {
                        [css.childPillSelected]: isSelected,
                        [css.childPillIneligible]: !eligible,
                      })}
                      aria-pressed={isSelected}
                      disabled={!eligible}
                      onClick={() => setAttendeeChildId(child.id)}
                    >
                      {isSelected ? <CheckIcon /> : null}
                      {child.firstName}
                    </button>
                  );
                })}
              </div>
              {someIneligible && !noneEligible ? (
                <p className={css.agePillHint}>
                  <FormattedMessage
                    id="ClassDetailPage.someOutsideAgeRange"
                    values={{ minAge: ageBandMin, maxAge: ageBandMax }}
                  />
                </p>
              ) : null}
            </>
          ) : null}
        </div>

        {priceFormatted ? (
          <div className={css.bookingTotalCard}>
            <span className={css.priceUnit}>
              <FormattedMessage id="ClassDetailPage.totalLabel" />
            </span>
            <span className={css.priceAmount}>{priceFormatted}</span>
            <span className={css.priceUnit}>
              {isTerm && !isOneSession && termWeeks(listing?.attributes?.publicData) ? (
                <FormattedMessage
                  id="ClassDetailPage.priceUnitTermWeeks"
                  values={{ weeks: termWeeks(listing?.attributes?.publicData) }}
                />
              ) : (
                <FormattedMessage
                  id={
                    isTerm && !isOneSession
                      ? 'ClassDetailPage.priceUnitTerm'
                      : 'ClassDetailPage.priceUnitSession'
                  }
                />
              )}
            </span>
            {seatsLine != null ? (
              <span className={css.spotsLine}>
                <FormattedMessage
                  id="ClassDetailPage.slotSpotsLeft"
                  values={{ count: seatsLine }}
                />
              </span>
            ) : null}
          </div>
        ) : null}
      </div>

      <div className={css.bookingModalActions}>
        {noneEligible ? (
          <p className={css.advanceCapNote}>
            <FormattedMessage
              id="ClassDetailPage.noneInAgeRange"
              values={{ minAge: ageBandMin, maxAge: ageBandMax }}
            />
          </p>
        ) : null}
        {isTerm && term && !termWithinCap ? (
          <p className={css.advanceCapNote}>
            <FormattedMessage id="ClassDetailPage.advanceBookingCapNote" />
          </p>
        ) : null}
        <button
          type="button"
          className={css.bookNow}
          disabled={!canSubmit}
          onClick={handleBookNow}
        >
          <FormattedMessage id="ClassDetailPage.bookNow" />
        </button>
      </div>
    </Modal>
  );
};

export default ClassBookingModal;
